/**
 * Source discovery policy for onboarding briefs.
 *
 * A platform can be the requested publication target ("пост для Telegram")
 * or an existing source owned by the user ("я веду канал"). The latter needs
 * a link or an uploaded artifact before source analysis can build a grounded
 * fingerprint. This policy deliberately works with semantic cue categories,
 * rather than one customer's wording or a single named platform.
 */

const EXISTING_SOURCE_NOUN =
	'(?:канал(?:а|ы|е|ом)?|групп(?:а|ы|у|е|ой)?|сообществ(?:о|а|е|ом)?|аккаунт(?:а|ы|е|ом)?|страниц(?:а|ы|у|е|ой)?|блог(?:а|и|е|ом)?|сайт(?:а|ы|е|ом)?|рассылк(?:а|и|у|е|ой)?|профил(?:ь|я|и|е|ем)?|чат(?:а|ы|е|ом)?|клуб(?:а|ы|е|ом)?)';
const SOCIAL_PLATFORM =
	'(?:telegram|телеграм(?:е|а|у|ом)?|телег|тг|tg|vk|вк|вконтакте|вконтакт|youtube|ютуб|instagram|инстаграм|facebook|фейсбук|tiktok|тикток|одноклассники|дзен|max|(?:социальн(?:ая|ой|ую|ой)?\\s+)?сеть)';
const TOKEN_START = '(?:^|[^\\p{L}\\p{N}_])';
const TOKEN_END = '(?=$|[^\\p{L}\\p{N}_])';
const WORD_START = '(?<![\\p{L}\\p{N}_])';
const WORD_END = '(?![\\p{L}\\p{N}_])';

const EXISTENCE_CLAIM = new RegExp(
	`${TOKEN_START}(?:у\\s+(?:меня|нас)\\s+(?:(?:уже|ещ[её])\\s+)?есть|есть|имеется)\\s+(?:(?:уже|ещ[её])\\s+)?(?:${SOCIAL_PLATFORM}[\\s-]*)?${EXISTING_SOURCE_NOUN}${TOKEN_END}`,
	'iu'
);
const POSSESSIVE =
	'(?:мой|моя|мо[её]|мои|наш|наша|наше|наши|свой|своя|сво[её]|свои|моей|нашей|моего|нашего|компани[ия]|проекта|команды|бренда)';
const OPERATING_VERB =
	'(?:вед(?:у|[её]м)|управля(?:ю|ем)|развива(?:ю|ем)|поддержива(?:ю|ем)|администрир(?:ую|уем)|модерир(?:ую|уем)|публикую|выпускаю|работает|работаем|существует|активен|активна|пишу|делюсь|собираю)';
const COMMAND_VERB =
	'(?:подготов(?:ь|ьте)|напиш(?:и|ите)|сделай(?:те)?|создай(?:те)?|опубликуй(?:те)?|сгенерируй(?:те)?|нужен|нужна|нужно|хочу)';
const NEGATED_SOURCE = new RegExp(
	`${TOKEN_START}(?:нет|не\\s+(?:создан|создано|существует|веду|вед[её]м|зав[её]л|открыл)|пока\\s+нет|ещ[её]\\s+не)${WORD_END}[^.!?\\n]{0,80}${EXISTING_SOURCE_NOUN}${TOKEN_END}|${TOKEN_START}${EXISTING_SOURCE_NOUN}\\s+(?:(?:ещ[её]|пока)\\s+)?не\\s+(?:создан(?:о|а|ы)?|существ(?:ует)?|вед(?:у|[её]м)|зав[её]л|откр(?:ыл|ыла|ыли))${TOKEN_END}`,
	'iu'
);
const TARGET_SOURCE = new RegExp(
	`${TOKEN_START}${COMMAND_VERB}[^.!?\\n]{0,80}${WORD_START}(?:для|в|на)${WORD_END}\\s+(?:(?:${POSSESSIVE})\\s+)?(?:${SOCIAL_PLATFORM}[\\s-]*)?${EXISTING_SOURCE_NOUN}${TOKEN_END}`,
	'iu'
);
const POST_TARGET_SOURCE = new RegExp(
	`${TOKEN_START}[^.!?\\n]{0,20}${WORD_START}(?:пишу|публикую)${WORD_END}[^.!?\\n]{0,40}${WORD_START}пост(?:а|ы|ов)?${WORD_END}[^.!?\\n]{0,40}${WORD_START}для${WORD_END}\\s+(?:(?:${POSSESSIVE})\\s+)?(?:${SOCIAL_PLATFORM}[\\s-]*)?${EXISTING_SOURCE_NOUN}${TOKEN_END}`,
	'iu'
);
const SOURCE_INPUT_REFERENCE = new RegExp(
	`${TOKEN_START}(?:из|с|со|по\\s+материал(?:у|ам)|на\\s+основе)\\s+(?:(?:${POSSESSIVE})\\s+)?(?:${SOCIAL_PLATFORM}[\\s-]*)?${EXISTING_SOURCE_NOUN}${TOKEN_END}`,
	'iu'
);
const ACTION_BEFORE_SOURCE = new RegExp(
	`${TOKEN_START}${OPERATING_VERB}[^.!?\\n]{0,72}(?<!для\\s)(?:${SOCIAL_PLATFORM}[\\s-]*)?${EXISTING_SOURCE_NOUN}${TOKEN_END}`,
	'iu'
);
const SOURCE_BEFORE_ACTION = new RegExp(
	`${TOKEN_START}(?:${POSSESSIVE}\\s+)?(?:${SOCIAL_PLATFORM}[\\s-]*)?${EXISTING_SOURCE_NOUN}[^.!?\\n]{0,72}${OPERATING_VERB}${TOKEN_END}`,
	'iu'
);
const POSSESSIVE_SOURCE = new RegExp(
	`${TOKEN_START}${POSSESSIVE}\\s+(?:${SOCIAL_PLATFORM}[\\s-]*)?${EXISTING_SOURCE_NOUN}(?:\\s+(?!${SOCIAL_PLATFORM}${TOKEN_END})[^.!?\\n]{1,48})?${TOKEN_END}`,
	'iu'
);

const HAS_URL = /(?:https?:\/\/\S+|(?:t\.me|telegram\.me)\/\S+)/iu;
const SOURCE_URL_HOST =
	/(?:https?:\/\/)?(?:t\.me|telegram\.(?:me|org)|vk\.(?:com|ru)|m\.vk\.com)\b/iu;

const SOURCE_LINK = /(?:https?:\/\/[^\s<>()]+|(?:t\.me|telegram\.me)\/[^\s<>()]+)/giu;

const trimSourceLinkPunctuation = (value: string): string => value.replace(/[.,;!?]+$/gu, '');

export const normalizeOnboardingSourceLink = (value: string): string =>
	value.startsWith('http') ? value : `https://${value}`;

/**
 * Accept the short Telegram form people actually paste in chat and normalize
 * it before the importer receives the link. The visible example can therefore
 * stay as `t.me/...` without making the user add a protocol by hand.
 */
export const extractOnboardingSourceLinks = (value: string): string[] =>
	Array.from(
		new Set(
			(value.match(SOURCE_LINK) ?? [])
				.map(trimSourceLinkPunctuation)
				.map(normalizeOnboardingSourceLink)
				.filter(Boolean)
		)
	);

export const removeOnboardingSourceLinks = (value: string): string =>
	value.replace(SOURCE_LINK, ' ').replace(/\s+/gu, ' ').trim();

export const hasOnboardingSourceLink = (value: string): boolean =>
	extractOnboardingSourceLinks(value).length > 0;

const normalize = (value: string): string =>
	String(value ?? '')
		.replace(/[—–]/g, '-')
		.replace(/\s+/g, ' ')
		.trim();

/** Remove quoted examples so a quoted sentence is not treated as user intent. */
const withoutQuotedExamples = (value: string): string =>
	value.replace(/«[^»]*»|“[^”]*”|"[^"]*"/gu, ' ');

const sentences = (value: string): string[] =>
	withoutQuotedExamples(value)
		.replace(/(?:https?:\/\/|(?:t\.me|telegram\.me)\/)[^\s<>()]+/giu, (url) =>
			url.replace(/\./g, '\u0000')
		)
		.split(/[.!?;\n]+/u)
		.map((item) => item.trim())
		.map((item) => item.replace(/\u0000/g, '.'))
		.filter(Boolean);

const sentenceHasSourceReference = (sentence: string): boolean => {
	if (!HAS_URL.test(sentence)) return false;
	// A source-specific host is strong evidence for a social channel. For a
	// generic site/blog, require the URL to follow the source noun or a colon;
	// an unrelated catalogue/competitor link must not suppress the question.
	if (SOURCE_URL_HOST.test(sentence)) return true;
	return new RegExp(
		`(?:${EXISTING_SOURCE_NOUN})[^:]{0,18}:\\s*https?:\\/\\/\\S+|https?:\\/\\/\\S+[^,]{0,18}(?:${EXISTING_SOURCE_NOUN})`,
		'iu'
	).test(sentence);
};

/** True when the brief claims an existing source, rather than a target. */
export const mentionsExistingOnboardingSource = (value: string): boolean => {
	const normalized = normalize(value);
	if (!normalized) return false;
	return sentences(normalized).some((sentence) => {
		if (
			NEGATED_SOURCE.test(sentence) ||
			TARGET_SOURCE.test(sentence) ||
			POST_TARGET_SOURCE.test(sentence)
		)
			return false;
		if (EXISTENCE_CLAIM.test(sentence)) return true;
		if (SOURCE_INPUT_REFERENCE.test(sentence)) return true;
		if (ACTION_BEFORE_SOURCE.test(sentence) || SOURCE_BEFORE_ACTION.test(sentence)) return true;
		// A possessive source needs a real description or operating context. A
		// bare "наш канал в Telegram" is ambiguous and remains a publication
		// target; "наш блог о дизайне" and "наш сайт работает" are not.
		if (POSSESSIVE_SOURCE.test(sentence)) {
			const source =
				new RegExp(
					`(?:${POSSESSIVE})\\s+(?:${SOCIAL_PLATFORM}[\\s-]*)?${EXISTING_SOURCE_NOUN}\\s+(.+)${TOKEN_END}`,
					'iu'
				).exec(sentence)?.[1] ?? '';
			return (
				Boolean(source.trim()) &&
				!new RegExp(`^(?:в|на)\\s+${SOCIAL_PLATFORM}${TOKEN_END}`, 'iu').test(source.trim())
			);
		}
		return false;
	});
};

/** True when the user declines to provide an existing source and asks to continue without it. */
export const isOnboardingSourceReferenceDeclined = (value: string): boolean => {
	const normalized = withoutQuotedExamples(normalize(value));
	if (!normalized) return false;
	return /^(?:нет|нет\s+ничего|ничего\s+нет|нечего\s+добавить)[.!?]*$/iu.test(normalized) || /(?:не\s+(?:хочу|могу|буду|стану|пришлю|дам|отправлю|предоставлю)|без\s+(?:ссылк|источник|эт(?:ого|их)|материал|соцсет|публикац)|(?:нет|пока\s+нет)\s*,?\s*продолж(?:им|ать)\s+(?:по|с)\s+(?:моему\s+)?описани\w*|(?:нет|пока\s+нет)\s+(?:(?:сайт|соцсет|публикац|ссылк|материал|источник)(?:\s+\w+){0,5}\s+и\s+)?(?:соцсет|публикац|ссылк|материал|источник|сайт)|(?:нет|пока\s+нет)\s+(?:сайт|соцсет|публикац|ссылк|материал|источник)(?:\s+и\s+[^.!?;]{1,80})?|продолж(?:им|ать)\s+без|давай(?:те)?\s+(?:без|с\s+нуля)|нач(?:нём|нем|ать)\s+с\s+нуля|с\s+нуля|пропуст(?:им|ить))/iu.test(
		normalized
	);
};

/**
 * Ask for a source reference before analysis. A URL or attached file is an
 * already actionable reference, so it must continue through the normal
 * importer/fingerprint path. An unrelated URL in another sentence does not
 * satisfy the source requirement.
 */
export const shouldRequestOnboardingSourceReference = ({
	value,
	hasFiles = false
}: {
	value: string;
	hasFiles?: boolean;
}): boolean => {
	if (hasFiles) return false;
	const normalized = normalize(value);
	if (!mentionsExistingOnboardingSource(normalized)) return false;
	return !sentences(normalized).some(sentenceHasSourceReference);
};
